<template>
    <div class="container">
        <div class="title-container">
            <span>{{ $t('reclamation.suivi.title') }}</span>
            <ReglePastille cle="suiviReclamation.definition" />
        </div>
        <div class="content-container">
            <div class='filter-container'>
                <Filter
                    @search="doFilter"
                    @addFilterStatus="this.addFilterStatus"
                    @removeFilterStatus="this.removeFilterStatus"
                    @clearFilter="this.clearFilter"
                    @statusToggleSelectAll="this.statusToggleSelectAll"
                    :filter="this.filter"
                    :statusList="this.statusList"
                    :searchSite="searchSite"
                />
                <ReglePastille cle="suiviReclamation.filtreStatut" />
            </div>
            <div class="dataTable">
                <div>
                    <h2>
                        {{ $tc('reclamation.suivi.dataTable.title', +count) }}
                    </h2>
                    <BorderedIconButton class="button-xls" v-if="!isExportDisabled" @click="exportXls" :imgUrl="iconeXls" :title="$t('list.export')"/>
                    <ReglePastille cle="suiviReclamation.export" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" @scroll="handleScroll" class="datatable-container">
                    <DataTable
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        :isLoading="isLoading"
                        :noDataText="$t('search.notFound')"
                        @sort="doSort"
                        @select="doSelect"
                    />
                </div>
            </div>
        </div>
    </div>

    <Modal v-if="this.isOpenHistoryModal" @cancel="this.historyModalCancel">
        <ModalCard
            :withoutBotNav="true"
            :headerTitle="$t('reclamation.suivi.history.modal.title', { id: this.historyId })"
            :activeValidate="false"
            @cancel="this.historyModalCancel"
        >
            <div class='height-1px'></div>
            <div class='container-modal'>
                <DataTable :tableHeader="this.headerHistoryTable" :tableData="this.dataHistoryTable" :noDataText="$t('reclamation.suivi.history.modal.no.data')" />
                <ReglePastille cle="suiviReclamation.clotureReouverture" />
                <div class='text-right margin-top-bottom-10px'>
                    <Button
                        :on-click='reouvrir'
                        :text="$t('reclamation.suivi.history.modal.reouvrir')"
                        v-if='getHistorique.length !== 0 && getHistorique[dataHistoryTable.length - 1].typeActionId === 4'
                    />
                    <Button
                        :on-click='cloturer'
                        :text="$t('reclamation.suivi.history.modal.cloturer')"
                        v-if='getHistorique.length === 0 || getHistorique[dataHistoryTable.length - 1].typeActionId !== 4'
                    />
                </div>
                <div v-if='afficherChampsReouverture'>
                    {{$t('reclamation.suivi.history.modal.motif.reouverture')}}
                    <div>
                        <textarea cols="75" rows="10" v-model="motifReouverture"/>
                    </div>
                    <div class='text-right margin-top-bottom-10px'>
                        <Button
                            :on-click='annulerReouverture'
                            :text="$t('reclamation.suivi.history.modal.annuler')"
                            class='width-75px margin-10px'
                        />
                        <ButtonBlue
                            :on-click='reouvrirReclamation'
                            :text="$t('reclamation.suivi.history.modal.valider')"
                            class='width-75px'
                        />
                    </div>
                </div>
            </div>
        </ModalCard>
    </Modal>

    <WaitingModal v-if='isExportXlsLoading || isClaimSending'/>
    <SuccessModal @cancel="exit" v-if="success" :subtitle="$t('intervention.success.subtitle')" :title="response" />

</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCard from '@/components/ui-components/ModalCard.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { SortType, sortValue } from '@/models/SortType';
import { EtatReclamation, HistoriqueReclamation, SuiviReclamation } from '@/models/SuiviReclamation';
import { TableHeader } from '@/models/TableHeader';
import Filter from '@/modules/Common/components/Filter.vue';
import { defineComponent } from 'vue';
import { SuiviReclamationActions } from '../store/action-types';
import { SUIVI_RECLAMATION } from '../store/mutation-types';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { store } from '@/plugins/store';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import Button from '@/components/ui-components/Button.vue';
import ReclamationService from '@/modules/Reclamation/services/ReclamationService';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    content: HTMLElement | null;
    statusList: string[];
    filter: {
        statusList: string[];
    };
    debounce: number;
    searchSite: string;
    isOpenHistoryModal: boolean;
    historyId: number;
    headerHistoryTable: any[];
    afficherChampsReouverture: boolean;
    motifReouverture: string;
    chantierId: number;
    adresseSiteId: number;
    response: string;
    isClaimSending: boolean;
    success: boolean;
}

export default defineComponent({
    name: 'SuiviReclamationPage',
    components: { SuccessModal, Button, ButtonBlue, WaitingModal, BorderedIconButton, Filter, DataTable, ModalCard, Modal, LoaderComponent, ReglePastille },
    data(): Data {
        return {
            content: null,
            statusList: Object.values(EtatReclamation),
            filter: {
                statusList: [],
            },
            debounce: 0,
            searchSite: '',
            isOpenHistoryModal: false,
            historyId: 0,
            headerHistoryTable: [
                {
                    label: this.$t('history.table.label.date'),
                    width: '10%',
                },
                {
                    label: this.$t('history.table.label.description'),
                    width: '80',
                },
                {
                    label: this.$t('history.table.label.state'),
                    width: '10%',
                },
            ],
            afficherChampsReouverture: false,
            motifReouverture: '',
            chantierId: 0,
            adresseSiteId: 0,
            response: '',
            isClaimSending: false,
            success: false,
        };
    },
    beforeMount() {
        this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
        this.$store.commit(SUIVI_RECLAMATION.SET_SORT, { sortData: this.tableHeader[0].sortData, sortStatus: this.tableHeader[0].sortStatus });
        this.getReclamationsData();
    },
    mounted() {
        if (!this.content) {
            this.content = document.querySelector('.container > .navigation-container + .content') as HTMLElement;
        }
        store.dispatch(SuiviReclamationActions.SET_RECLAMATIONS_CONSULTEES);

        if (this.content && !this.content.onscroll) this.content.onscroll = () => this.handleScroll(this.content);
        this.content?.scrollTo({ top: this.scroll });
    },
    unmounted() {
        if (this.content && this.content.onscroll) this.content.onscroll = null;
    },
    methods: {
        getReclamationsData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            this.$store.dispatch(SuiviReclamationActions.GET_SUIVI_RECLAMATION, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
            });
        },
        exportXls() : void {
            store.dispatch(SuiviReclamationActions.GET_XLS_RECLAMATION, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
            });
        },
        doFilter() {
            this.$store.commit(SUIVI_RECLAMATION.RESET_ITEMS);
            this.$store.commit(SUIVI_RECLAMATION.SET_FILTER_STATUS, this.filter.statusList);

            this.getReclamationsData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(SUIVI_RECLAMATION.SET_SORT, { sortData, sortStatus });
            this.getReclamationsData();
        },
        async doSelect(item: { idReclamation: number, statut: string, invisibleDateCloture: string, adresseSiteId: number, chantierId: number}) {
            this.isClaimSending = true;
            this.historyId = item.idReclamation;
            const reclamation = this.getSuiviReclamationItems.find((reclamation: any) => reclamation.id === item.idReclamation);

            this.adresseSiteId = reclamation.adresseSiteId;
            this.chantierId = reclamation.chantierId;
            await this.$store.dispatch(SuiviReclamationActions.GET_HISTORIQUE, {id: this.historyId, etat: item.statut, dateCloture: item.invisibleDateCloture});
            this.isClaimSending = false;
            this.isOpenHistoryModal = true;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        clearFilter() {
            this.filter.statusList = this.statusList ?? [];
            this.doFilter();
        },
        handleScroll(content: HTMLElement | null) {
            const top = content?.scrollTop ?? 0;
            const max = content?.scrollHeight ?? 0;
            const height = content?.clientHeight ?? 0;
            this.$store.commit(SUIVI_RECLAMATION.SET_SCROLL, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(SUIVI_RECLAMATION.SET_PAGE, this.page + 1);
                this.getReclamationsData(this.page);
            }
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        historyModalCancel() {
            this.isOpenHistoryModal = false;
        },
        reouvrir() {
            this.afficherChampsReouverture = true;
        },
        annulerReouverture() {
            this.afficherChampsReouverture = false;
            this.isOpenHistoryModal = false;
        },
        reouvrirReclamation() {
            this.isClaimSending = true;
            this.isOpenHistoryModal = false;
            ReclamationService.sendReclamation({
                    chantierId: this.chantierId,
                    adresseSiteId: this.adresseSiteId,
                    description: this.motifReouverture,
                    reclamationId: this.historyId})
                .then(async res => {
                    this.isClaimSending = false;
                    if (res.status === 200) {
                        this.response = this.$t('reclamation.success.title', {
                            idReclamation: res.data,
                        });
                        this.success = true;
                    } else {
                        this.$store.commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processErrorCustomMessage(res.status, 'app.error.undefined'),
                            { root: true }
                        );
                    }
                    this.motifReouverture = '';
                    this.afficherChampsReouverture = false;
                })
                .catch(async error => {
                    this.$store.commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        await ErrorManager.processError(error),
                        { root: true }
                    );
                    this.isClaimSending = false;
                });
        },
        exit() {
            this.success = false;
            this.afficherChampsReouverture = false;
            this.getReclamationsData();
        },
        async cloturer() {
            this.isClaimSending = true;
            this.isOpenHistoryModal = false;
            await ReclamationService.cloturerReclamation(this.historyId).catch(async error => {
                this.$store.commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processError(error),
                    { root: true }
                );
                this.isClaimSending = false;
            });
            this.isClaimSending = false;
            this.$store.commit(SUIVI_RECLAMATION.RESET_ITEMS);
            this.getReclamationsData();
        }
    },
    computed: {
        tableData() {
            return this.$store.getters.getSuiviReclamationItems.map(
                ({ id, description, etat, dateEmission, emetteur, destinataire, dateCloture }: SuiviReclamation) => {
                    return {
                        idReclamation: id,
                        dateEmission: dateEmission ? new Date(dateEmission).toLocaleDateString() : '',
                        description,
                        emetteur: emetteur,
                        destinataire: destinataire,
                        statut: statusName(etat),
                        invisibleDateCloture: dateCloture
                    };
                }
            );
        },
        getSuiviReclamationItems() {
            return this.$store.getters.getSuiviReclamationItems;
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters.getSuiviReclamationTableHeader;
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters.getSuiviReclamationSort;
        },
        page() {
            return this.$store.getters.getSuiviReclamationPage;
        },
        numberOfPages() {
            return this.$store.getters.getSuiviReclamationNbOfPages;
        },
        scroll() {
            return this.$store.getters.getSuiviReclamationScroll;
        },
        isLoading() {
            return this.$store.getters.getSuiviReclamationIsLoading;
        },
        filterSite(): any[] {
            return this.$store.getters.getSuiviReclamationFilterSite;
        },
        filterStatus(): Array<string> {
            return this.$store.getters.getSuiviReclamationFilterStatus;
        },
        count() {
            return this.$store.getters.getSuiviReclamationCount;
        },
        dataHistoryTable() {
            return this.$store.getters.getHistoriqueItems.map(
                ({ date, description, typeActionId }: HistoriqueReclamation) => {
                    const statut = typeActionId === 4 ? statusName('CLOTUREE') : statusName('EN_TRAITEMENT');
                    return {
                        date: date ? new Date(date).toLocaleDateString() : '',
                        description,
                        statut: statut,
                    };
                }
            );
        },
        getHistorique() {
            return this.$store.getters.getHistoriqueItems;
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return this.tableData.length === 0;
        },
        isExportXlsLoading(): boolean {
            return store.getters.getIsExportReclamationLoading;
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

@media screen and (max-width: 850px) {
    .datatable-container {
        max-height: calc(100vh - 460px);
    }
}
:deep(table tr td span){
    -webkit-box-orient: unset;
    margin-bottom: 10px;
    margin-top: 10px;
}
.button-xls {
    height: 24px;
}
button {
    height: 38px;
    border-radius: 6px;
    cursor: pointer;
}
.height-1px {
    height: 1px;
}
.container-modal {
    max-height: 59vh;
    overflow: auto;
}
</style>
